import { useCallback, useEffect, useRef, useState } from 'react';
import { arcToFixed } from '@src/core/extensions';

export type PolygonVertexCoordKey = 'lat' | 'lng';

function draftKey(i: number, key: PolygonVertexCoordKey) {
  return `${i}-${key}`;
}

export type PolygonVertex = { lat?: number | string; lng?: number | string };

export function usePolygonVertexInputs(options: {
  vertexCount: number;
  setPolygonVertex: (index: number, key: PolygonVertexCoordKey, n: number) => void;
}) {
  const { vertexCount, setPolygonVertex } = options;
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const prevCountRef = useRef(vertexCount);

  useEffect(() => {
    if (vertexCount !== prevCountRef.current) {
      setDrafts({});
      prevCountRef.current = vertexCount;
    }
  }, [vertexCount]);

  const displayVertexCoord = useCallback(
    (i: number, key: PolygonVertexCoordKey, stored: number | undefined) => {
      const k = draftKey(i, key);
      if (drafts[k] !== undefined) return drafts[k];
      if (stored == null || Number.isNaN(Number(stored))) return '';
      return String(stored);
    },
    [drafts]
  );

  const onVertexCoordChange = useCallback((i: number, key: PolygonVertexCoordKey, raw: string) => {
    const v = raw.replace(',', '.');
    if (v !== '' && !/^-?\d*\.?\d*$/.test(v)) return;
    setDrafts((prev) => ({ ...prev, [draftKey(i, key)]: v }));
  }, []);

  const onVertexCoordBlur = useCallback(
    (i: number, key: PolygonVertexCoordKey, raw: string) => {
      const k = draftKey(i, key);
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[k];
        return next;
      });
      const trimmed = raw.replace(',', '.').trim();
      if (
        trimmed === '' ||
        trimmed === '-' ||
        trimmed === '.' ||
        trimmed === '-.'
      ) {
        return;
      }
      const n = parseFloat(arcToFixed(trimmed, 6) || 'NaN');
      if (Number.isNaN(n)) return;
      setPolygonVertex(i, key, n);
    },
    [setPolygonVertex]
  );

  /** Apply in-progress edits so submit works without blurring each field first. */
  const buildCoordinatesForSubmit = useCallback((vertices: PolygonVertex[]) => {
    const next = vertices.map((p) => ({
      lat: Number(p?.lat),
      lng: Number(p?.lng),
    }));
    Object.entries(draftsRef.current).forEach(([k, raw]) => {
      const m = /^(\d+)-(lat|lng)$/.exec(k);
      if (!m) return;
      const index = Number(m[1]);
      const coordKey = m[2] as PolygonVertexCoordKey;
      const row = next[index];
      if (!row) return;
      const trimmed = raw.replace(',', '.').trim();
      if (
        trimmed === '' ||
        trimmed === '-' ||
        trimmed === '.' ||
        trimmed === '-.'
      ) {
        return;
      }
      const n = parseFloat(arcToFixed(trimmed, 6) || 'NaN');
      if (!Number.isNaN(n)) {
        row[coordKey] = n;
      }
    });
    return next;
  }, []);

  return {
    displayVertexCoord,
    onVertexCoordChange,
    onVertexCoordBlur,
    buildCoordinatesForSubmit,
  };
}
